import { Link, useNavigate } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageHeader, Pager, Panel, RowsSkeleton, SearchBox, StatusBadge } from "@/components/app/kit";
import { useFetch, useServerList } from "@/hooks/useServerList";
import { fmtDate } from "@/lib/i18n";

const Filter = ({ value, onChange, options, all, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="sm:w-44 bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Tenants() {
  const navigate = useNavigate();
  const ref = useFetch("/platform/reference");
  const list = useServerList("/platform/tenants", { status: "all", plan: "all", country: "all" });
  const plans = Object.fromEntries((ref.data?.plans || []).map((p) => [p.code, p.name]));
  return (
    <div>
      <PageHeader eyebrow="Gestion" title="Cabinets médicaux" subtitle="Tous les espaces cabinets de la plateforme."
        actions={<Button asChild data-testid="tenants-create-button"><Link to="/admin/cabinets/nouveau"><Plus className="h-4 w-4 mr-2" />Créer un cabinet</Link></Button>} />
      <Panel className="overflow-hidden">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 -mt-1 mb-4">
          <SearchBox value={list.filters.search} onChange={(v) => list.setFilter("search", v)} placeholder="Nom, identifiant, ville…" testid="tenants-search" />
          <Filter value={list.filters.status} onChange={(v) => list.setFilter("status", v)} all="Tous les statuts" testid="tenants-status-filter"
            options={[["active", "Actif"], ["trial", "Essai"], ["suspended", "Suspendu"], ["expired", "Expiré"]]} />
          <Filter value={list.filters.plan} onChange={(v) => list.setFilter("plan", v)} all="Toutes les formules" testid="tenants-plan-filter" options={Object.entries(plans)} />
          <Filter value={list.filters.country} onChange={(v) => list.setFilter("country", v)} all="Tous les pays" testid="tenants-country-filter"
            options={(ref.data?.countries || []).map((c) => [c.code, c.name])} />
        </div>
        <div className="-mx-5 overflow-x-auto">
          {list.loading ? <RowsSkeleton /> : list.items.length === 0 ? (
            <EmptyState icon={Building2} title="Aucun cabinet" text="Aucun cabinet ne correspond à votre recherche."
              action={<Button asChild variant="outline"><Link to="/admin/cabinets/nouveau">Créer un cabinet</Link></Button>} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Cabinet</TableHead><TableHead>Identifiant</TableHead><TableHead>Ville</TableHead><TableHead>Formule</TableHead>
                <TableHead>Utilisateurs</TableHead><TableHead>Échéance</TableHead><TableHead>Statut</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {list.items.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => navigate(`/admin/cabinets/${t.id}`)} data-testid={`tenant-row-${t.slug}`}>
                    <TableCell><p className="font-semibold text-sm">{t.name}</p><p className="text-xs text-slate-500">{t.commercial_name}</p></TableCell>
                    <TableCell><span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200">{t.slug}</span></TableCell>
                    <TableCell className="text-sm">{t.city} <span className="text-slate-400">· {t.country_code}</span></TableCell>
                    <TableCell className="text-sm">{plans[t.plan_code] || t.plan_code}</TableCell>
                    <TableCell className="text-sm font-mono">{t.users_count} / {t.limits?.max_users}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(t.end_date)}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="-mx-5 -mb-5"><Pager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} /></div>
      </Panel>
    </div>
  );
}
